import { anyRarity, townsperson } from './content.js';
import { FOREST_DRAGON_QUESTS } from './forest-dragon.js';
/**
 * The quests of the Forêt des Murmures (ADR 0047): the chain that starts with
 * la Garde-forestière Ysolde (`ranger`, in `mobs.ts`, placed on the square of
 * Sylvebois by the generator), the townsfolk it needs, its two baits, and the
 * experience of its creatures once the chain pays the rest. See `content.ts`.
 *
 * The story: the forest whispers because it hurts. Goblins burn its groves,
 * the treants wake in anger, an ash-grey wolf is born of the fires, and under
 * the full moon something howls in the Clairière de la Lune. Two threads, the
 * trees (the woodcutter, the druid, the goblins) and the wolves (the trapper),
 * meet at the woodcutter's lost son, who saw the werewolf; then the boss, and
 * east to the swamp.
 */
/** The townsfolk only this region's quests need, added to the bestiary. */
export const FOREST_NPC_IDS = [
    'forest-woodcutter',
    'forest-druid',
    'forest-trapper',
    'forest-boy',
];
const RANGER = { mob: 'ranger', biome: 'forest' };
const SMITH = { mob: 'smith', biome: 'forest' };
const ALCHEMIST = { mob: 'alchemist', biome: 'forest' };
const MERCHANT = { mob: 'merchant', biome: 'forest' };
const WOODCUTTER = { mob: 'forest-woodcutter', biome: 'forest' };
const DRUID = { mob: 'forest-druid', biome: 'forest' };
const TRAPPER = { mob: 'forest-trapper', biome: 'forest' };
const BOY = { mob: 'forest-boy', biome: 'forest' };
const SWAMP_ELDER = { mob: 'swamp-elder', biome: 'swamp' };
const QUESTS = [
    {
        id: 'forest-arrival',
        name: 'Les murmures de Sylvebois',
        biome: 'forest',
        level: 5,
        giver: RANGER,
        turnIn: RANGER,
        offer: 'Tu entends ? Il n’y a pas de vent, et pourtant les feuilles bougent. Ça dure depuis un mois et c’est pire la nuit. Avant d’aller dans les bois, touche l’arche de la place, puis va dire bonjour au forgeron et à l’alchimiste. Tu vas avoir besoin d’eux.',
        summary: 'Enregistrer le portail de Sylvebois, saluer le forgeron et l’alchimiste, puis revenir à Ysolde.',
        pending: 'L’arche est au centre de la place, la forge au nord-est et l’alchimiste au sud. Et ne cueille pas de champignons en chemin !',
        complete: 'Tu as marché sur trois champignons en venant. Je les ai entendus d’ici. Il va falloir apprendre à marcher sans bruit, en forêt !',
        objectives: [
            { kind: 'portal', label: 'Enregistrer le portail de Sylvebois', portal: 'forest' },
            {
                kind: 'talk',
                label: 'Saluer le forgeron',
                npc: SMITH,
                line: 'Tu viens de Havrebourg ? Ça se voit, ils tapent le métal à froid, là-bas. Ici, on le durcit avec de la résine. Rapporte-moi de la résine ambrée, celle qui colle aux doigts pendant une semaine, et je te ferai du vrai travail.',
            },
            {
                kind: 'talk',
                label: 'Saluer l’alchimiste',
                npc: ALCHEMIST,
                line: 'Des oreilles de gobelin, oui oui. Ne fais pas cette tête, ça soigne très bien ! Rapporte-moi ce que tu trouves sur les bêtes de la forêt et je t’en ferai des potions. Le goût, on s’y habitue.',
            },
        ],
        reward: { xp: 50, gold: 10 },
    },
    {
        id: 'forest-hood',
        name: 'La capuche du chasseur',
        biome: 'forest',
        level: 5,
        requires: ['forest-arrival'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Enlève-moi ce chapeau de paille, on te voit de l’autre bout de la forêt ! Ici, les chasseurs portent une capuche, les loups sentent moins ton odeur. J’ai mis vingt ans à comprendre ça. Achète une capuche, mets-la, et je te rembourse une partie.',
        summary: 'Acheter une Capuche du chasseur chez le marchand de Sylvebois et la porter.',
        pending: 'Achète la capuche et mets-la sur ta tête. Bien rabattue, pas posée sur l’épaule comme les ados.',
        complete: 'Ah, là tu ressembles à un chasseur ! De loin. Et de dos. C’est un début.',
        objectives: [
            { kind: 'wear', label: 'Porter une Capuche du chasseur', items: anyRarity('hunter-helm') },
        ],
        reward: { xp: 50, gold: 10 },
    },
    {
        id: 'forest-wolves',
        name: 'Les crocs de la lisière',
        biome: 'forest',
        level: 5,
        requires: ['forest-arrival'],
        giver: RANGER,
        turnIn: RANGER,
        offer: 'Des loups sont venus jusqu’à la palissade cette nuit. Ils ne viennent jamais si près, d’habitude ! Quelque chose les fait fuir du fond de la forêt. Chasse-les et rapporte-moi quelques peaux, je veux les regarder de près.',
        summary: 'Chasser les loups autour de Sylvebois et rapporter leurs peaux à Ysolde.',
        pending: 'Les loups chassent en groupe. Garde un arbre dans ton dos pour ne pas te faire encercler.',
        complete: 'Regarde leur dos, les poils sont brûlés ! Comme s’ils avaient couru à travers un feu. Il n’y a pas eu d’incendie par ici, pourtant. Bizarre… Tiens, pour ta peine.',
        objectives: [
            { kind: 'kill', label: 'Loups chassés', mobs: ['wolf'], count: 12 },
            { kind: 'collect', label: 'Peaux de loup', item: 'wolf-pelt', count: 4 },
        ],
        reward: { xp: 130, gold: 25, items: [{ templateId: 'forest-tonic', quantity: 3 }] },
    },
    {
        id: 'forest-woodcutter',
        name: 'Les bûcherons du Mont Moussu',
        biome: 'forest',
        level: 5,
        requires: ['forest-wolves'],
        giver: RANGER,
        turnIn: WOODCUTTER,
        offer: 'Anselme, le bûcheron, coupe son bois au pied du Mont Moussu, à l’ouest. D’habitude, il descend vendre son bois toutes les semaines, et là, plus de nouvelles. En plus, des gobelins archers tirent sur tous ceux qui prennent son chemin. Va voir s’il va bien, et occupe-toi des archers au passage.',
        summary: 'Rejoindre Anselme le bûcheron au pied du Mont Moussu, en abattant les archers gobelins qui rôdent autour de son camp.',
        pending: 'Anselme campe près de la grotte, au pied du Mont Moussu, à l’ouest. Les archers sont au sud. Cours-leur dessus, ils sont nuls au corps à corps.',
        complete: 'Ha ! Enfin quelqu’un qui ne me tire pas dessus ! Ça fait trois jours que je me cache derrière cette souche. Mais ce n’est pas le pire, l’ami : mes arbres se sont mis à marcher ! Et mon fils Tobie n’est pas rentré depuis trois nuits.',
        objectives: [
            { kind: 'kill', label: 'Archers gobelins abattus', mobs: ['goblin-archer'], count: 5 },
        ],
        reward: { xp: 50, gold: 10 },
    },
    {
        id: 'forest-treants',
        name: 'Les arbres qui marchent',
        biome: 'forest',
        level: 6,
        requires: ['forest-woodcutter'],
        giver: WOODCUTTER,
        turnIn: WOODCUTTER,
        offer: 'Trente ans que je coupe des chênes, et jamais un ne m’avait rendu le coup ! Ces arbres vivants, les sylvains, se sont réveillés et ils frappent fort. Bats-en quelques-uns et rapporte-moi leur écorce, je veux comprendre ce qui leur arrive. Pour Tobie, il dort sûrement chez les charbonniers, il fait souvent ça.',
        summary: 'Abattre des sylvains dans la forêt et rapporter leur écorce à Anselme.',
        pending: 'Il y a des sylvains à l’est et au sud-ouest de mon camp. Ils sont lents : frappe, puis recule avant qu’ils ne frappent.',
        complete: 'Regarde ça, l’écorce est brûlée de l’intérieur ! Un arbre, ça ne brûle pas par le cœur, normalement. Prends mes gants, ils ont tenu ma hache pendant vingt hivers. Moi, je n’ai plus trop envie de couper du bois.',
        objectives: [
            { kind: 'kill', label: 'Sylvains abattus', mobs: ['treant'], count: 8 },
            { kind: 'collect', label: 'Écorces de sylvain', item: 'treant-bark', count: 3 },
        ],
        reward: { xp: 150, gold: 30, items: [{ templateId: 'hunter-gloves-rare', quantity: 1 }] },
    },
    {
        id: 'forest-druid',
        name: 'La voix des Ramures',
        biome: 'forest',
        level: 6,
        requires: ['forest-treants'],
        giver: WOODCUTTER,
        turnIn: DRUID,
        offer: 'Il faut demander à Mélisande, la druidesse. Elle vit en haut du Pic des Ramures, au nord, on y monte par la grotte. Elle ne m’aime pas beaucoup, je coupe ses arbres. Mais montre-lui l’écorce, elle t’écoutera.',
        summary: 'Gravir le Pic des Ramures par sa grotte, au nord, et parler à la Druidesse Mélisande.',
        pending: 'Le Pic des Ramures est au nord. Entre dans la grotte au pied du pic et monte jusqu’en haut.',
        complete: 'Montre-moi ça. Chut, laisse-moi sentir… L’écorce sent la résine brûlée, et autre chose que je ne connais pas. Les gobelins brûlent mes bois, ça je le savais déjà. Mais ça, ce n’est pas un gobelin qui l’a fait.',
        objectives: [
            { kind: 'reach', label: 'Gravir le Pic des Ramures', landmark: 'Pic des Ramures' },
        ],
        reward: { xp: 60, gold: 12 },
    },
    {
        id: 'forest-smith',
        name: 'L’acier de Sylvebois',
        biome: 'forest',
        level: 6,
        requires: ['forest-hood'],
        giver: SMITH,
        turnIn: SMITH,
        offer: 'Le forgeron de Havrebourg t’a monté ça à deux crans ? Contre un sylvain, ça va rebondir. Rapporte-moi de la résine et de l’or, et on passe à trois crans. Attention, après deux, ça peut rater. Prévois de quoi recommencer.',
        summary: 'Faire porter une pièce d’équipement à +3 par un forgeron.',
        pending: 'Il me faut une pièce à trois crans, sur toi. Si ça rate, pas de panique, on recommence.',
        complete: 'Tu entends comme ça sonne bien maintenant ? Non ? Tant pis. Prends cette résine, j’en ai fait cuire beaucoup trop.',
        objectives: [{ kind: 'upgrade', label: 'Une pièce portée à +3', level: 3 }],
        reward: { xp: 100, gold: 20, items: [{ templateId: 'amber-resin', quantity: 2 }] },
    },
    {
        id: 'forest-ashes',
        name: 'Les cendres du bosquet',
        biome: 'forest',
        level: 6,
        requires: ['forest-druid'],
        giver: DRUID,
        turnIn: DRUID,
        offer: 'Tu vois ces trois fumées ? Ce sont les gobelins : ils brûlent la forêt pour attraper le gibier et voler le miel des ruches. Et chaque fois qu’un arbre brûle, un sylvain se réveille en colère. Chasse les gobelins et rapporte-moi leurs oreilles.',
        summary: 'Chasser les gobelins qui brûlent la forêt et rapporter leurs oreilles à Mélisande.',
        pending: 'Les gobelins sont surtout au sud-est, en petites bandes. Frappe avant qu’ils te voient.',
        complete: 'Plus que deux fumées, merci ! Prends ces potions. C’est fait avec des racines, ça a un goût de terre, mais bois-les quand même.',
        objectives: [
            { kind: 'kill', label: 'Gobelins chassés', mobs: ['goblin'], count: 12 },
            { kind: 'collect', label: 'Oreilles de gobelin', item: 'goblin-ear', count: 4 },
        ],
        reward: { xp: 160, gold: 30, items: [{ templateId: 'forest-potion', quantity: 2 }] },
    },
    {
        id: 'forest-camp',
        name: 'Le Camp des Gobelins',
        biome: 'forest',
        level: 7,
        requires: ['forest-ashes'],
        giver: DRUID,
        turnIn: DRUID,
        offer: 'Tu vois les tentes, juste en dessous ? Les gobelins ont osé installer leur camp sur MA montagne ! Ils gardent un coffre plein de choses volées. Bats les quatre gardiens et ouvre le coffre. Il y a d’autres camps dans la forêt, dont un en haut du Mont Moussu, si tu préfères.',
        summary: 'Vaincre les gardiens du camp gobelin dressé sur le Pic des Ramures, ou d’un autre camp de la forêt, et ouvrir son coffre.',
        pending: 'Le camp est juste à côté. Le coffre ne s’ouvre que quand les quatre gardiens sont battus.',
        complete: 'Oh, la hachette d’Anselme ! Je la lui rendrai en douce, il ne saura jamais d’où elle vient. Les bottes, garde-les, elles sont pour toi.',
        objectives: [
            { kind: 'camp', label: 'Coffre d’un camp de la forêt', biome: 'forest', count: 1 },
        ],
        reward: { xp: 190, gold: 40, items: [{ templateId: 'hunter-boots-rare', quantity: 1 }] },
    },
    {
        id: 'forest-chief',
        name: 'Le Chef gobelin',
        biome: 'forest',
        level: 7,
        requires: ['forest-camp'],
        giver: DRUID,
        turnIn: DRUID,
        offer: 'Les gobelins ont un chef, et il change de cachette tout le temps. Mais il adore ce qui brille ! Prends ce sac de perles en verre et ouvre-le près d’un repaire de gobelins. Il ne pourra pas résister. Attention, il frappe plus fort que les autres.',
        summary: 'Ouvrir la bourse de verroterie près d’un repaire de gobelins de la forêt, puis abattre le Chef gobelin qui accourt.',
        pending: 'Ouvre la bourse près d’un repaire de gobelins, loin du village. S’il s’enfuit, recommence.',
        complete: 'Sans leur chef, les gobelins vont se disputer entre eux, et on aura enfin la paix. Il y a d’autres grosses bêtes comme lui dans la forêt, avec leur nom en couleur. Elles cachent souvent de beaux trésors.',
        grants: [{ templateId: 'goblin-bait', quantity: 1 }],
        objectives: [{ kind: 'kill', label: 'Chef gobelin abattu', mobs: ['goblin-chief'], count: 1 }],
        reward: { xp: 200, gold: 40, items: [{ templateId: 'forest-potion', quantity: 2 }] },
    },
    {
        id: 'forest-trapper',
        name: 'Le trappeur de la Tanière',
        biome: 'forest',
        level: 7,
        requires: ['forest-ashes'],
        giver: DRUID,
        turnIn: TRAPPER,
        offer: 'Depuis les incendies, il y a un loup gris avec la meute, que je n’avais jamais vu avant. Corvin, le trappeur, le suit depuis des jours, près de la Tanière des Loups, au sud-est. Va le voir. Et ne crie pas son nom, il déteste ça.',
        summary: 'Rejoindre le trappeur Corvin près de la Tanière des Loups.',
        pending: 'La Tanière des Loups est au sud-est. Corvin campe un peu à l’ouest de la tanière. Il est très discret, c’est lui qui te trouvera.',
        complete: 'Chut ! Baisse-toi, tu vas faire fuir le gibier. C’est la druidesse qui t’envoie ? Bon. Ça fait dix jours que je suis ce loup gris. Il est couleur cendre, et il ne laisse aucune trace dans la boue. C’est pas normal.',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Tanière des Loups', landmark: 'Tanière des Loups' },
        ],
        reward: { xp: 50, gold: 10 },
    },
    {
        id: 'forest-ash-wolf',
        name: 'Le Loup cendré',
        biome: 'forest',
        level: 7,
        requires: ['forest-trapper'],
        giver: TRAPPER,
        turnIn: TRAPPER,
        offer: 'Il sent mes pièges de loin, ce malin. Par contre, il adore la viande fumée. Pose ce morceau près d’une tanière de loups, il viendra. Mais d’abord, chasse un peu sa meute, il ne sort jamais sans elle.',
        summary: 'Éclaircir la meute, puis poser la viande fumée près d’une tanière de loups et abattre le Loup cendré.',
        pending: 'Chasse d’abord la meute, puis pose la viande près d’une tanière de loups. Il est gris cendre, tu le reconnaîtras.',
        complete: 'Dix jours que je le suivais, et c’est toi qui l’as eu ! Prends ce bouclier, je l’ai taillé dans un sylvain tombé. Les crocs glissent dessus.',
        grants: [{ templateId: 'ash-bait', quantity: 1 }],
        objectives: [
            { kind: 'kill', label: 'Loups de la meute', mobs: ['wolf'], count: 8 },
            { kind: 'kill', label: 'Loup cendré abattu', mobs: ['ash-wolf'], count: 1 },
        ],
        reward: { xp: 210, gold: 40, items: [{ templateId: 'hunter-shield-rare', quantity: 1 }] },
    },
    {
        id: 'forest-silver-fang',
        name: 'Croc-d’Argent',
        biome: 'forest',
        level: 8,
        requires: ['forest-ash-wolf'],
        giver: TRAPPER,
        turnIn: TRAPPER,
        offer: 'Il y a plus coriace encore : Croc-d’Argent, le plus vieux loup de la forêt. Je le chasse depuis que je suis gamin. Aucun appât ne marche avec lui, sauf ce sifflet. Souffle dedans près d’une tanière de loups, il viendra. Fais attention à tes doigts, il m’en a pris deux.',
        summary: 'Souffler dans le sifflet près d’une tanière de loups et abattre Croc-d’Argent.',
        pending: 'Souffle dans le sifflet près d’une tanière de loups. Parfois, il passe aussi tout seul, d’un repaire à l’autre.',
        complete: 'C’est bien lui ! Trente ans que j’attends ce jour. Prends cette lame, je l’avais fabriquée exprès pour le jour où on l’aurait. Elle est à toi.',
        grants: [{ templateId: 'silver-whistle', quantity: 1 }],
        objectives: [{ kind: 'kill', label: 'Croc-d’Argent abattu', mobs: ['silver-fang'], count: 1 }],
        reward: { xp: 240, gold: 50, items: [{ templateId: 'silver-fang-blade', quantity: 1 }] },
    },
    {
        id: 'forest-lost-son',
        name: 'Le fils du bûcheron',
        biome: 'forest',
        level: 7,
        requires: ['forest-ash-wolf', 'forest-chief'],
        giver: TRAPPER,
        turnIn: BOY,
        offer: 'Autre chose : en suivant le loup gris, j’ai vu des traces de petits pieds nus qui partaient vers le Roc aux Loups, au sud-ouest. C’est sûrement Tobie, le fils d’Anselme ! On monte par la grotte au pied du rocher. Fais vite.',
        summary: 'Gravir le Roc aux Loups par sa grotte, au sud-ouest, et y retrouver Tobie.',
        pending: 'Le Roc aux Loups est au sud-ouest, au bord de la forêt. Passe par la grotte pour monter.',
        complete: 'Va-t’en ! Euh… attends, t’es pas le monsieur-loup ? Dans la Clairière de la Lune, il y avait un monsieur. La lune s’est levée et pouf, il s’est transformé en loup ! Il m’a mordu au bras, et j’ai couru jusqu’ici.',
        objectives: [{ kind: 'reach', label: 'Gravir le Roc aux Loups', landmark: 'Roc aux Loups' }],
        reward: { xp: 70, gold: 14 },
    },
    {
        id: 'forest-remedy',
        name: 'La morsure de la lune',
        biome: 'forest',
        level: 7,
        requires: ['forest-lost-son'],
        giver: BOY,
        turnIn: WOODCUTTER,
        offer: 'Mon bras me brûle, et la nuit j’ai envie de hurler à la lune. C’est bizarre. La druidesse et l’alchimiste sauront peut-être me soigner. Et dis à papa que je vais bien ! Mais je ne descends pas tant que le loup est en bas.',
        summary: 'Demander un remède à la druidesse et à l’alchimiste, puis porter des nouvelles de Tobie à son père.',
        pending: 'Va voir la druidesse et l’alchimiste, puis papa. Papa, il est au pied du Mont Moussu.',
        complete: 'Il est vivant ! Ouf ! Sur le Roc aux Loups ? Ce petit chenapan, il grimpe partout depuis qu’il sait marcher. Et il ne veut pas descendre tant que la bête est là ? Il a bien raison. C’est bien mon fils, ça !',
        objectives: [
            {
                kind: 'talk',
                label: 'Demander conseil à la druidesse',
                npc: DRUID,
                line: 'Une morsure de loup-garou… Tobie ne guérira vraiment que quand la bête sera vaincue. En attendant, de la sève du vieux chêne calmera sa fièvre. L’alchimiste sait la préparer, va le voir.',
            },
            {
                kind: 'talk',
                label: 'Faire préparer le remède',
                npc: ALCHEMIST,
                line: 'De la sève du vieux chêne ? La dernière fois qu’on m’en a demandé, j’avais encore des cheveux ! Voilà la fiole. Trois gouttes le soir, pas quatre. Dis-le bien à la druidesse, elle a la main lourde.',
            },
        ],
        reward: { xp: 80, gold: 16, items: [{ templateId: 'forest-potion', quantity: 2 }] },
    },
    {
        id: 'forest-moon-ready',
        name: 'Paré pour la pleine lune',
        biome: 'forest',
        level: 7,
        requires: ['forest-remedy'],
        giver: WOODCUTTER,
        turnIn: RANGER,
        offer: 'Il faut prévenir Ysolde. Mais pas avec ce que tu as à la ceinture, tu ne ferais pas peur à un lapin ! Achète le coutelas du chasseur chez le marchand, et prends un bon bouclier. Corvin t’en a peut-être déjà donné un. C’est Ysolde qui sait où se cache le loup-garou.',
        summary: 'Porter le Coutelas et le Bouclier du chasseur, puis rapporter à Ysolde ce que Tobie a vu.',
        pending: 'Le coutelas est chez le marchand de Sylvebois. Il va essayer de te le vendre trop cher, négocie !',
        complete: 'Un loup-garou ! Et Tobie s’est fait mordre… Moi qui cherchais un simple loup. Bon, tu es bien équipé, pour une fois. Assieds-toi, je vais te dire où il se cache.',
        objectives: [
            { kind: 'wear', label: 'Porter un Coutelas du chasseur', items: anyRarity('hunter-sword') },
            { kind: 'wear', label: 'Porter un Bouclier du chasseur', items: anyRarity('hunter-shield') },
        ],
        reward: { xp: 80, gold: 16 },
    },
    {
        id: 'forest-werewolf',
        name: 'La Clairière de la Lune',
        biome: 'forest',
        level: 8,
        requires: ['forest-moon-ready'],
        giver: RANGER,
        turnIn: RANGER,
        offer: 'Il se cache dans un vieux sanctuaire, sous la Clairière de la Lune, au sud. Il y dort le jour. Descends, bats-le, et emmène des amis si tu peux, il est costaud.',
        summary: 'Abattre le Loup-Garou au fond de son donjon, sous la Clairière de la Lune, puis revenir à Ysolde.',
        pending: 'La Clairière de la Lune est au sud. Prends des potions, beaucoup de potions.',
        complete: 'Écoute… Plus rien ! Les feuilles ne bougent plus. Je vais enfin pouvoir dormir. Tobie peut redescendre ! Prends cette armure en cuir, elle était à mon père. Ah, et Mélisande te cherche. Elle dit qu’elle a entendu des arbres mourir tous en même temps, à la Saignée.',
        objectives: [{ kind: 'kill', label: 'Loup-Garou abattu', mobs: ['werewolf'], count: 1 }],
        reward: { xp: 300, gold: 60, items: [{ templateId: 'hunter-chest-rare', quantity: 1 }] },
    },
    {
        id: 'forest-cub',
        name: 'Le renardeau du trappeur',
        biome: 'forest',
        level: 8,
        requires: ['forest-werewolf'],
        giver: RANGER,
        turnIn: TRAPPER,
        offer: 'Avant de partir, passe voir Corvin. Il a sauvé un bébé renard d’un piège gobelin, et depuis, le renardeau ne quitte plus son camp. Corvin dit qu’il attend quelqu’un. Et Corvin ne parle jamais pour rien dire.',
        summary: 'Rejoindre le trappeur Corvin à la Tanière des Loups et recevoir le renardeau.',
        pending: 'La Tanière des Loups est au sud-est. Corvin campe un peu à l’ouest de la tanière. Il est très discret, c’est lui qui te trouvera.',
        complete: 'Regarde ses oreilles, il t’a senti arriver ! C’est toi qu’il attendait. Prends-le avec toi. Il marchera à ta droite et ramassera ce que tu fais tomber. Bien mieux que toi, sûrement.',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Tanière des Loups', landmark: 'Tanière des Loups' },
        ],
        reward: { xp: 40, gold: 8, items: [{ templateId: 'forest-fox-cub', quantity: 1 }] },
    },
    {
        id: 'forest-east',
        name: 'La route des brumes',
        biome: 'forest',
        level: 8,
        requires: ['forest-recruit'],
        giver: RANGER,
        turnIn: SWAMP_ELDER,
        offer: 'Mélisande est partie avec ton chevalier, la forêt est bien calme sans elle. Les souches disent que le dragon volait vers le sud-est, vers les marais. Va à Brumevase, après Havrebourg. La Doyenne du village m’écrit des lettres de plus en plus inquiètes, et elle n’a jamais peur de rien.',
        summary: 'Traverser le pays vers l’est jusqu’à Brumevase et parler à la Doyenne des Brumes.',
        pending: 'Brumevase est loin à l’est, au cœur du marais. Passe par l’arche de Havrebourg, ça ira plus vite.',
        complete: 'Approche, approche, mes yeux ne voient plus grand-chose dans cette brume. C’est Ysolde qui t’envoie ? Elle m’écrit trois lignes par an, et cette année, il y avait ton nom dedans. Tu dois être quelqu’un d’important !',
        objectives: [{ kind: 'reach', label: 'Atteindre Brumevase', landmark: 'Brumevase' }],
        reward: { xp: 50, gold: 10 },
    },
];
export const FOREST = {
    // The dragon’s branch before the road east, so the chain still ends on its hand-over.
    quests: [...QUESTS.slice(0, -1), ...FOREST_DRAGON_QUESTS, ...QUESTS.slice(-1)],
    npcs: {
        'forest-woodcutter': townsperson('forest-woodcutter', 'Bûcheron Anselme'),
        'forest-druid': townsperson('forest-druid', 'Druidesse Mélisande'),
        'forest-trapper': townsperson('forest-trapper', 'Trappeur Corvin'),
        'forest-boy': townsperson('forest-boy', 'Tobie'),
    },
    keepers: [
        // By the cave at the foot of the Mont Moussu, where he fells his oaks.
        { mob: 'forest-woodcutter', near: 'Mont Moussu', dx: 4, dy: 14, facing: 'south' },
        // On the summit of the Pic des Ramures, beside its trapdoor: reached by the cave.
        { mob: 'forest-druid', near: 'Pic des Ramures', dx: 2, dy: 2, facing: 'south' },
        // West of the Wolves' Den, out of the pack's reach.
        { mob: 'forest-trapper', near: 'Tanière des Loups', dx: -9, dy: 5, facing: 'east' },
        // Hiding on the summit of the Roc aux Loups, where no wolf climbs.
        { mob: 'forest-boy', near: 'Roc aux Loups', dx: 2, dy: 1, facing: 'south' },
        // Each player's own quarry at the Saignée (ADR 0087): no den lies near it.
        {
            mob: 'treant-maddened',
            near: 'La Saignée',
            dx: 0,
            dy: 0,
            count: 4,
            radius: 5,
            during: 'forest-dragon-scar',
        },
        {
            mob: 'goblin',
            near: 'La Saignée',
            dx: 0,
            dy: 3,
            count: 5,
            radius: 5,
            during: 'forest-dragon-stumps',
        },
    ],
    items: [
        {
            id: 'ash-bait',
            name: 'Quartier de viande fumée',
            kind: 'consumable',
            maxStack: 1,
            icon: 'icon.ash-bait',
            lootable: false,
            bait: { summons: 'ash-wolf', den: 'wolf' },
        },
        {
            id: 'goblin-bait',
            name: 'Bourse de verroterie',
            kind: 'consumable',
            maxStack: 1,
            icon: 'icon.goblin-bait',
            lootable: false,
            bait: { summons: 'goblin-chief', den: 'goblin' },
        },
        {
            id: 'silver-whistle',
            name: 'Sifflet d’argent',
            kind: 'consumable',
            maxStack: 1,
            icon: 'icon.silver-whistle',
            lootable: false,
            bait: { summons: 'silver-fang', den: 'wolf' },
        },
    ],
    // A little lower again for the dragon's branch (ADR 0067), under what the
    // swamp's weakest pays and over what the meadow's strongest does.
    xp: { wolf: 13, goblin: 14, treant: 15, 'goblin-archer': 14 },
    greetings: {
        ranger: 'Parle moins fort, sous les arbres. Tout le monde t’entend.',
        'forest-woodcutter': 'Chêne, hêtre, sapin… Je les reconnais tous à l’odeur, les yeux fermés !',
        'forest-druid': 'Chut… il y a un oiseau qui fait son nid, juste là. Ne le dérange pas.',
        'forest-trapper': 'Baisse-toi, tu fais fuir le gibier !',
        'forest-boy': 'J’ai presque plus mal au bras ! Et je ne hurle plus à la lune. Enfin, presque plus.',
    },
};
//# sourceMappingURL=forest.js.map